import { useTechnicalCategories } from '@hooks';
import { IOption, TechnicalCategories, TechnicalCategoryType } from '@models';
import { useStore } from '@stores';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import Presentation from './TechnicalCategoryLookup.presentation';

interface IProps {
  vin: string;
  setCategory: (category: string) => void;
  setActiveKey: (value: string) => void;
}

export const TechnicalCategoryLookup: React.FC<IProps> = observer(
  ({ vin, setCategory, setActiveKey }) => {
    const { technicalCategoryStore } = useStore();
    const { categories, setName, handleNameSearch } = useTechnicalCategories({ vin });

    const handleClear = () => {
      setName('');
      technicalCategoryStore.clearPagedCategories();
    };

    const handleSelect = useCallback(
      async (id: string) => {
        const found = categories.find((x) => x.id === id)!;
        const category = found.category as TechnicalCategoryType;
        await technicalCategoryStore.getSubCategoriesByCategoryId({ id });
        await technicalCategoryStore.getCategoriesByVinAndCategory({ vin, category }).then(() => {
          setActiveKey('4');
          setCategory(category);
        });
      },
      [vin, categories, technicalCategoryStore, setCategory, setActiveKey]
    );

    const filtered = useMemo(
      () => [...TechnicalCategories].filter((x) => categories.map((y) => y.category).includes(x)),
      [categories]
    );

    const options: IOption[] = useMemo(
      () =>
        filtered.map((techCategory, index) => {
          return {
            key: `lookup${techCategory}${index}`,
            value: techCategory,
            label: techCategory,
            options: categories
              .filter((x) => x.category === techCategory)
              .map((category) => {
                return {
                  label: category.name,
                  value: category.id,
                  key: category.id,
                };
              }),
          };
        }),
      [categories, filtered]
    );

    return (
      <Presentation
        options={options}
        handleClear={handleClear}
        handleNameSearch={handleNameSearch}
        handleSelect={handleSelect}
      />
    );
  }
);
